// src/components/issues/tableau/valeurs.tsx — SCE Issues
// Comment le tableau de « Tous les problèmes » montre une valeur : dans une
// cellule, et dans la liste d'un filtre. Un seul endroit, pour que le filtre
// « Statut » montre exactement les badges de la colonne Statut.

import { Badge } from '@/components/ui/Badge'
import {
  CriticiteLibellee,
  EtatProbleme,
  LIBELLE_STATUT_LISTE,
  StatutLibelle,
} from '@/components/ui/EtatProbleme'
import { METIERS_COULEURS } from '@/config/metiers.config'
import { estSujet } from '@/types/issue.types'
import { libelleCle, metierDeCle, type ClePerimetre } from '@/config/sujets.config'
import { DEFINITIONS_BADGES } from '@/config/badgesCloture.config'
import {
  A_QUALIFIER,
  libelleBadge,
  type BadgeCloture,
  type Criticite,
  type Issue,
  type Statut,
} from '@/types/issue.types'
import { ageEnJours, type ColonneId, type ContexteTableau } from '@/utils/tableauProblemes'

export function dateCourte(iso: string | undefined): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

/** Un métier, ou un sujet (GOULOTTE : point Méca cerclé, il se lit « dans » la Méca). */
export function PastilleMetier({ metier }: { metier: ClePerimetre }): JSX.Element {
  const couleur = METIERS_COULEURS[metierDeCle(metier)]
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          flex: 'none',
          ...(estSujet(metier) ? { border: `2px solid ${couleur}`, backgroundColor: 'var(--color-white)' } : { backgroundColor: couleur }),
        }}
      />
      {libelleCle(metier)}
    </span>
  )
}

/** Un badge de clôture ou, s'ils sont plusieurs, le premier suivi de « +N ». */
export function BadgesCompacts({ issue }: { issue: Issue }): JSX.Element | null {
  const [premier, ...suivants] = issue.badgesCloture ?? []
  if (!premier) return null
  return (
    <span
      className="inline-flex items-center gap-1"
      title={(issue.badgesCloture ?? []).map((b) => libelleBadge(issue, b)).join(' · ')}
    >
      <Badge badgeCloture={premier} court />
      {suivants.length > 0 && (
        <span style={{ fontSize: 'var(--font-size-tag)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
          +{suivants.length}
        </span>
      )}
    </span>
  )
}

/** Le contenu d'une cellule du tableau. */
export function ContenuCellule({
  col,
  issue,
  ctx,
  extrait = false,
}: {
  col: ColonneId
  issue: Issue
  ctx: ContexteTableau
  /** Colonne Problème : un extrait de la description à la suite du titre. */
  extrait?: boolean
}): JSX.Element | null {
  switch (col) {
    case 'id':
      return <span className="tableau-secondaire">{issue.id}</span>
    case 'titre': {
      const description = extrait ? issue.description.replace(/\s+/g, ' ').trim() : ''
      return (
        <span className="tableau-titre-bloc" title={description ? `${issue.titre} — ${description}` : issue.titre}>
          <span className="tableau-titre">{issue.titre}</span>
          {description && <span className="tableau-extrait"> — {description}</span>}
        </span>
      )
    }
    case 'metier':
      return <PastilleMetier metier={issue.metier} />
    case 'machine':
      return <>{issue.numeroSerie}</>
    case 'of':
      return <span className="tableau-secondaire">{issue.of}</span>
    case 'pn':
      return <span className="tableau-secondaire">{issue.pn}</span>
    case 'modele':
      return <span title={ctx.modeleDe(issue)}>{ctx.modeleDe(issue)}</span>
    case 'famille':
      return issue.famille === A_QUALIFIER ? <span className="tableau-a-qualifier">À qualifier</span> : <>{issue.famille}</>
    case 'phase':
      return <>{issue.phase}</>
    case 'criticite':
      // « À qualifier » est déjà dit par le statut : la case reste vide.
      return issue.statut === 'Ouvert' || issue.statut === 'Annulé' ? null : <CriticiteLibellee criticite={issue.criticite} />
    case 'statut':
      return <StatutLibelle statut={issue.statut} />
    case 'etat':
      return <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
    case 'badge':
      return <BadgesCompacts issue={issue} />
    case 'declarant':
      return <>{issue.declarantNom}</>
    case 'date':
      return <span className="tableau-secondaire">{dateCourte(issue.dateDeclaration)}</span>
    case 'age':
      return <span className="tableau-secondaire">{ageEnJours(issue, ctx.maintenant)} j</span>
    case 'cloture':
      return issue.dateCloture ? <span className="tableau-secondaire">{dateCourte(issue.dateCloture)}</span> : null
  }
}

/** Une valeur dans la liste d'un filtre. */
export function LibelleValeur({ col, valeur }: { col: ColonneId; valeur: string }): JSX.Element {
  switch (col) {
    case 'metier':
      return <PastilleMetier metier={valeur as ClePerimetre} />
    case 'criticite':
      return <CriticiteLibellee criticite={valeur as Criticite} />
    case 'statut':
      return <StatutLibelle statut={valeur as Statut} />
    case 'badge':
      return (DEFINITIONS_BADGES as Record<string, unknown>)[valeur] ? (
        <Badge badgeCloture={valeur as BadgeCloture} />
      ) : (
        <>{valeur}</>
      )
    default:
      return <>{valeur}</>
  }
}

/** Texte brut d'une valeur (pastilles de filtres actifs, infobulles). */
export function texteValeur(col: ColonneId, valeur: string): string {
  if (col === 'metier') return libelleCle(valeur as ClePerimetre) ?? valeur
  if (col === 'statut') return LIBELLE_STATUT_LISTE[valeur as Statut] ?? valeur
  return valeur
}
